import React from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Brain,
  Sparkles,
  User,
  Building,
  FileText,
  ArrowRight,
  Moon,
  Sun,
} from "lucide-react";
import {
  interviewActions,
} from "../../../Redux/ActionCreator.js";
import { ApiService } from "../api.service";
import LoadingOverlay from "../common/LoadingOverlay";
import ErrorMessage from "../common/ErrorMessage";
import { ToastContainer, toast } from "react-toastify";

const api = new ApiService();

const InterviewSetupPage = () => {
  const dispatch = useDispatch();
  const flowLanguage = useSelector((e) => e.t_lang.selected);
  const { interviewConfig, ui } = useSelector((state) => state.interview);
  const { darkMode } = ui.settings;
  const { loading, error } = ui;

  const theme = {
    bg: darkMode
      ? "bg-gray-900"
      : "bg-gradient-to-br from-blue-50 to-indigo-100",
    card: darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200",
    text: darkMode ? "text-gray-100" : "text-gray-900",
    textSecondary: darkMode ? "text-gray-400" : "text-gray-600",
    button: darkMode
      ? "bg-blue-600 hover:bg-blue-700"
      : "bg-blue-600 hover:bg-blue-700",
    buttonSecondary: darkMode
      ? "bg-gray-700 hover:bg-gray-600"
      : "bg-gray-100 hover:bg-gray-200",
  };

  const handleConfigChange = (field, value) => {
    dispatch(interviewActions.setInterviewConfig({ [field]: value }));
  };

  const handleGenerateQuestions = async () => {
    if (!interviewConfig.role || !interviewConfig.company) {
      // dispatch(interviewActions.setError("Please fill in all required fields"));
      toast.warn("Please fill in all required fields", {
        position: toast.POSITION.TOP_CENTER,
      });
      return;
    }

    try {
      dispatch(interviewActions.generateQuestionsStart());

      const response = await api.generateQuestions({
        ...interviewConfig,
        language: flowLanguage,
      });

      console.log("response", response);

      if (response.data) {
        dispatch(interviewActions.generateQuestionsSuccess(response.data));
      } else {
        throw new Error("Invalid response format");
      }
    } catch (error) {
      console.error("Error generating questions:", error);
      dispatch(
        interviewActions.generateQuestionsError(
          "Failed to generate questions. Please try again."
        )
      );
      toast.warn("Failed to generate questions. Please try again.", {
        position: toast.POSITION.TOP_CENTER,
      });
      // dispatch(interviewActions.generateQuestionsError());
    }
  };

  const toggleDarkMode = () => {
    dispatch(interviewActions.setDarkMode(!darkMode));
  };

  const dismissError = () => {
    dispatch(interviewActions.clearError());
  };

  return (
    <div className={`min-h-screen ${theme.bg} transition-all duration-300`}>
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <div className="text-center flex-1">
              <div className="flex items-center justify-center space-x-2 mb-4">
                <Brain className="h-8 w-8 text-blue-600" />
                <h1 className={`text-4xl font-bold ${theme.text}`}>
                  AI Interview Practice
                </h1>
                <Sparkles className="h-8 w-8 text-yellow-500" />
              </div>
              <p className={`${theme.textSecondary} text-lg`}>
                Master your interview skills with AI-powered feedback and
                analytics
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={toggleDarkMode}
                className={`p-3 rounded-lg ${theme.buttonSecondary} transition-all duration-200`}
              >
                {darkMode ? (
                  <Sun className="h-5 w-5" />
                ) : (
                  <Moon className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <ErrorMessage
              message={error}
              onDismiss={dismissError}
              className="mb-6"
            />
          )}

          {/* Configuration Form */}
          <div className={`${theme.card} rounded-xl shadow-lg border p-6 mb-6`}>
            <h2 className={`text-2xl font-semibold ${theme.text} mb-6`}>
              Interview Configuration
            </h2>

            <div className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    className={`block text-sm font-medium ${theme.text} mb-2`}
                  >
                    <User className="inline h-4 w-4 mr-1" />
                    Target Role *
                  </label>
                  <input
                    type="text"
                    value={interviewConfig.role}
                    onChange={(e) => handleConfigChange("role", e.target.value)}
                    className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 ${
                      darkMode
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white"
                    }`}
                    placeholder="e.g., Senior Software Engineer"
                    required
                  />
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium ${theme.text} mb-2`}
                  >
                    <Building className="inline h-4 w-4 mr-1" />
                    Company *
                  </label>
                  <input
                    type="text"
                    value={interviewConfig.company}
                    onChange={(e) =>
                      handleConfigChange("company", e.target.value)
                    }
                    className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 ${
                      darkMode
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white"
                    }`}
                    placeholder="e.g., Google, Microsoft"
                    required
                  />
                </div>
              </div>

              {/* Additional Settings */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label
                    className={`block text-sm font-medium ${theme.text} mb-2`}
                  >
                    Experience Level
                  </label>
                  <select
                    value={interviewConfig.level}
                    onChange={(e) =>
                      handleConfigChange("level", e.target.value)
                    }
                    className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 ${
                      darkMode
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white"
                    }`}
                  >
                    <option value="Junior">Junior</option>
                    <option value="Mid">Mid-Level</option>
                    <option value="Senior">Senior</option>
                    <option value="Lead">Lead/Principal</option>
                  </select>
                </div>

                {/* <div>
                  <label
                    className={`block text-sm font-medium ${theme.text} mb-2`}
                  >
                    Domain
                  </label>
                  <select
                    value={interviewConfig.domain}
                    onChange={(e) =>
                      handleConfigChange("domain", e.target.value)
                    }
                    className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 ${
                      darkMode
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white"
                    }`}
                  >
                    <option value="Technology">Technology</option>
                    <option value="Finance">Finance</option>
                    <option value="Healthcare">Healthcare</option>
                    <option value="Marketing">Marketing</option>
                    <option value="Sales">Sales</option>
                  </select>
                </div> */}

                <div>
                  <label
                    className={`block text-sm font-medium ${theme.text} mb-2`}
                  >
                    Questions Count
                  </label>
                  <select
                    value={interviewConfig.count}
                    onChange={(e) =>
                      handleConfigChange("count", parseInt(e.target.value))
                    }
                    className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 ${
                      darkMode
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white"
                    }`}
                  >
                    <option value={5}>5 Questions</option>
                    <option value={10}>10 Questions</option>
                    <option value={15}>15 Questions</option>
                  </select>
                </div>
              </div>

              {/* Job Description */}
              <div>
                <label
                  className={`block text-sm font-medium ${theme.text} mb-2`}
                >
                  <FileText className="inline h-4 w-4 mr-1" />
                  Job Description (Optional)
                </label>
                <textarea
                  value={interviewConfig.description}
                  onChange={(e) =>
                    handleConfigChange("description", e.target.value)
                  }
                  rows={4}
                  className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 ${
                    darkMode
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white"
                  }`}
                  placeholder="Paste the job description here for more targeted questions..."
                />
              </div>
            </div>

            {/* Generate Button */}
            <div className="mt-8 flex justify-end">
              <button
                onClick={handleGenerateQuestions}
                disabled={
                  !interviewConfig.role ||
                  !interviewConfig.company ||
                  loading.isLoading
                }
                className={`px-8 py-3 ${theme.button} text-white rounded-lg font-medium transition-all duration-200 transform hover:scale-105 disabled:bg-gray-400 disabled:cursor-not-allowed disabled:transform-none flex items-center shadow-lg`}
              >
                <Sparkles className="h-5 w-5 mr-2" />
                Generate Questions
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
        <ToastContainer />
      </div>
      <ToastContainer />
      {loading.isLoading && (
        <LoadingOverlay message={loading.message} darkMode={darkMode} />
      )}
    </div>
  );
};

export default InterviewSetupPage;
